import { Button, Flex, Text } from "@radix-ui/themes";
import type { Message } from "ai";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import Lottie from "react-lottie";
import type { Options as LottieOptions } from "react-lottie";
import { toast } from "sonner";
import improveChatLoader from "@/lottie/improve_chat_loader.json";
import {
  SimulatorWrapperContext,
  TemporaryPolicyOverride,
} from "../../context/SimulatorWrapper";
import { ChatWrapperContext } from "../../context/ChatWrapper";
import { useImproveChat } from "./ImproveChatWrapper";
import { useTopicReleasePolicyApi } from "@/v2/entities/topic-release-policy/TopicReleasePolicy.api";
import { useInstructionApi } from "@/v2/entities/instruction/Instruction.api";
import { usePrerequisiteApi } from "@/v2/entities/prerequisite/Prerequisite.api";
import { TOOL_TYPES } from "./ImproveChat.model";
import styles from "./ToolCard.module.scss";

const LottieDisplay = Lottie as any;

const GENERIC_CONTENT_KEYS = [
  "content",
  "instruction",
  "prerequisite",
  "new_text",
  "text",
  "brief",
  "new_brief",
] as const;

type BriefEditMode = "replace" | "add";

const isBriefEditMode = (value?: string | null): value is BriefEditMode => {
  if (!value) {
    return false;
  }
  const normalized = value.toLowerCase();
  return normalized === "replace" || normalized === "add";
};

const getStringValue = (value: unknown): string | undefined => {
  return typeof value === "string" ? value : undefined;
};

const buildBriefFromSuggestion = (
  existingBrief: string,
  args: Record<string, unknown>
): string => {
  const baseBrief = typeof existingBrief === "string" ? existingBrief : "";
  const modeValue = getStringValue(args.mode);
  const mode = isBriefEditMode(modeValue)
    ? (modeValue as BriefEditMode)
    : undefined;
  const anchorText = getStringValue(args.anchor_text);
  const replacementText = getStringValue(args.new_text);

  if (!mode) {
    throw new Error("Brief suggestion must include mode");
  }

  if (mode === "replace") {
    if (!anchorText) {
      throw new Error("Brief suggestion is missing anchor text");
    }

    if (!baseBrief) {
      throw new Error("Current brief is empty, nothing to replace");
    }

    const anchorIndex = baseBrief.indexOf(anchorText);
    if (anchorIndex === -1) {
      throw new Error("Anchor text was not found in the current brief");
    }

    const replacement = replacementText ?? "";

    return (
      baseBrief.slice(0, anchorIndex) +
      replacement +
      baseBrief.slice(anchorIndex + anchorText.length)
    );
  }

  if (mode === "add") {
    if (!replacementText || replacementText.length === 0) {
      throw new Error("Add suggestions must provide new_text");
    }

    if (!baseBrief) {
      return replacementText;
    }

    if (!anchorText) {
      return `${baseBrief}\n${replacementText}`;
    }

    const anchorIndex = baseBrief.indexOf(anchorText);
    if (anchorIndex === -1) {
      return `${baseBrief}\n${replacementText}`;
    }

    const insertPosition = anchorIndex + anchorText.length;
    return (
      baseBrief.slice(0, insertPosition) +
      replacementText +
      baseBrief.slice(insertPosition)
    );
  }

  return baseBrief;
};

const getGenericSuggestionContent = (args: Record<string, unknown>): string => {
  for (const key of GENERIC_CONTENT_KEYS) {
    const candidate = getStringValue(args[key]);
    if (candidate !== undefined) {
      return candidate;
    }
  }
  return "";
};

export interface ToolCardProps {
  toolName: string;
  args: Record<string, any>;
  isDisabled?: boolean;
}

export function ToolCard({
  toolName,
  args,
  isDisabled = false,
}: ToolCardProps) {
  const simulatorContext = useContext(SimulatorWrapperContext);
  const chatContext = useContext(ChatWrapperContext);
  const {
    messages,
    topicId,
    releaseId,
    improveChatId,
    setMessages,
    setIsDisabled,
  } = useImproveChat();
  const { clearTemporaryPolicyOverride, refreshReleaseData } =
    simulatorContext || {};
  const topics = simulatorContext?.topics ?? [];

  const [isLoading, setIsLoading] = useState(false);
  const [topicReleasePolicyId, setTopicReleasePolicyId] = useState<
    string | null
  >(null);

  const topicReleasePolicyApi = useTopicReleasePolicyApi();
  const instructionApi = useInstructionApi();
  const prerequisiteApi = usePrerequisiteApi();

  const isSuggestionTool = useMemo(() => {
    return (
      toolName === TOOL_TYPES.SUGGEST_BRIEF_EDIT ||
      toolName === TOOL_TYPES.SUGGEST_INSTRUCTION_ADD ||
      toolName === TOOL_TYPES.SUGGEST_PREREQUISITE_ADD
    );
  }, [toolName]);

  // Fetch topic release policy ID for instruction/prerequisite creation
  useEffect(() => {
    const fetchTopicReleasePolicy = async () => {
      if (!topicId || !releaseId) {
        return;
      }

      try {
        const policy = await topicReleasePolicyApi.getTopicReleasePolicy(
          topicId,
          releaseId
        );
        if (policy) {
          setTopicReleasePolicyId(policy.id);
        }
      } catch {
        // Silently fail - policy ID will be checked before use
      }
    };

    fetchTopicReleasePolicy();
  }, [topicId, releaseId, topicReleasePolicyApi]);

  // Helper to add improveChatId to payload
  const withImproveChatId = useCallback(
    <T extends Record<string, unknown>>(
      payload: T
    ): T & { improveChatId?: string } => {
      if (!improveChatId) {
        return payload as T & { improveChatId?: string };
      }
      return {
        ...payload,
        improveChatId,
      };
    },
    [improveChatId]
  );

  const topicReleasePolicies = simulatorContext?.topicReleasePolicies ?? [];

  const getTopicBrief = useCallback(
    (targetTopicId?: number | null) => {
      if (!targetTopicId) {
        return "";
      }

      const policy = topicReleasePolicies.find(
        (candidate) => candidate.topicId === targetTopicId
      );

      return policy?.brief ?? "";
    },
    [topicReleasePolicies]
  );

  const currentTopicBrief = useMemo(
    () => getTopicBrief(topicId),
    [getTopicBrief, topicId]
  );

  const getTopicLabel = useCallback(
    (targetTopicId?: number | null) => {
      if (!targetTopicId) {
        return "this topic";
      }
      const topic = topics.find((candidate) => candidate.id === targetTopicId);
      if (topic?.topic) {
        return `topic "${topic.topic}"`;
      }
      return `topic ${targetTopicId}`;
    },
    [topics]
  );

  const buildChangeSummary = useCallback(() => {
    const locationLabel = getTopicLabel(topicId);

    if (toolName === TOOL_TYPES.SUGGEST_BRIEF_EDIT) {
      return `Brief updated for ${locationLabel}.`;
    }

    if (toolName === TOOL_TYPES.SUGGEST_INSTRUCTION_ADD) {
      return `Instruction added to ${locationLabel}.`;
    }

    if (toolName === TOOL_TYPES.SUGGEST_PREREQUISITE_ADD) {
      return `Prerequisite added to ${locationLabel}.`;
    }

    return "";
  }, [toolName, topicId, getTopicLabel]);

  const appendSummaryMessage = useCallback(
    (summary: string) => {
      if (!summary) {
        return;
      }

      const changeMessage: Message = {
        id: `save-summary-${Date.now()}`,
        role: "assistant",
        content: summary,
        createdAt: new Date(),
      };

      setMessages([...messages, changeMessage]);
    },
    [messages, setMessages]
  );

  // Save handlers for each tool type
  const handleBriefEdit = useCallback(async () => {
    if (!topicId || !releaseId) {
      throw new Error("Topic ID or Release ID not available");
    }

    const updatedBrief = buildBriefFromSuggestion(
      currentTopicBrief ?? "",
      args as Record<string, unknown>
    ).trim();

    const params = withImproveChatId({
      topicId,
      releaseId,
      brief: updatedBrief,
    });

    await topicReleasePolicyApi.updateTopicReleasePolicy(
      topicId,
      releaseId,
      params
    );
    return "Brief updated successfully";
  }, [
    args,
    topicId,
    releaseId,
    topicReleasePolicyApi,
    withImproveChatId,
    currentTopicBrief,
  ]);

  const handleInstructionAdd = useCallback(async () => {
    if (!topicReleasePolicyId) {
      throw new Error("Topic release policy ID not available");
    }

    const content = args.content || args.instruction || "";
    if (!content) {
      throw new Error("Instruction content not available");
    }

    const params = withImproveChatId({
      content: content.trim(),
      topicReleasePolicyId,
    });

    await instructionApi.createInstruction(params);
    return "Instruction added successfully";
  }, [args, topicReleasePolicyId, instructionApi, withImproveChatId]);

  const handlePrerequisiteAdd = useCallback(async () => {
    if (!topicReleasePolicyId) {
      throw new Error("Topic release policy ID not available");
    }

    const content = args.content || args.prerequisite || "";
    if (!content) {
      throw new Error("Prerequisite content not available");
    }

    const params = withImproveChatId({
      content: content.trim(),
      topicReleasePolicyId,
    });

    await prerequisiteApi.createPrerequisite(params);
    return "Prerequisite added successfully";
  }, [args, topicReleasePolicyId, prerequisiteApi, withImproveChatId]);

  // Handle Save Changes button
  const handleSaveChanges = useCallback(async () => {
    setIsLoading(true);

    try {
      let successMessage = "";

      if (toolName === TOOL_TYPES.SUGGEST_BRIEF_EDIT) {
        successMessage = await handleBriefEdit();
      } else if (toolName === TOOL_TYPES.SUGGEST_INSTRUCTION_ADD) {
        successMessage = await handleInstructionAdd();
      } else if (toolName === TOOL_TYPES.SUGGEST_PREREQUISITE_ADD) {
        successMessage = await handlePrerequisiteAdd();
      } else {
        throw new Error("Unsupported tool type");
      }

      // Clear temporary override and refresh data
      clearTemporaryPolicyOverride?.();
      await refreshReleaseData?.();

      // Show success toast
      toast.success(successMessage);

      const summaryDescription = buildChangeSummary();
      appendSummaryMessage(summaryDescription);

      // Disable the improve chat
      setIsDisabled(true);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to save changes";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [
    toolName,
    handleBriefEdit,
    handleInstructionAdd,
    handlePrerequisiteAdd,
    clearTemporaryPolicyOverride,
    refreshReleaseData,
    buildChangeSummary,
    appendSummaryMessage,
    setIsDisabled,
  ]);

  const handlePreview = useCallback(() => {
    if (!simulatorContext || !chatContext) return;

    const topicIdFromArgs =
      args.topicId || topicId || chatContext.selectedTopic?.id;

    if (!topicIdFromArgs) {
      console.warn("Cannot preview: missing topicId");
      return;
    }

    let content = "";

    try {
      if (toolName === TOOL_TYPES.SUGGEST_BRIEF_EDIT) {
        content = buildBriefFromSuggestion(
          getTopicBrief(topicIdFromArgs),
          args as Record<string, unknown>
        ).trim();
      } else {
        content = getGenericSuggestionContent(
          args as Record<string, unknown>
        ).trim();
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to preview changes";
      toast.error(message);
      return;
    }

    if (!content) {
      console.warn("Cannot preview: missing content");
      return;
    }

    // Add AI message to improve chat immediately
    const aiMessage = {
      id: `ai-message-${Date.now()}`,
      role: "assistant" as const,
      content:
        "Observe the new generation in the chat and decide whether to save or keep editing.",
      createdAt: new Date(),
    };

    setMessages([...messages, aiMessage]);

    // Determine override type based on tool name
    let overrideType: "brief_edit" | "instruction_add" | "prerequisite_add";
    if (toolName === TOOL_TYPES.SUGGEST_BRIEF_EDIT) {
      overrideType = "brief_edit";
    } else if (toolName === TOOL_TYPES.SUGGEST_INSTRUCTION_ADD) {
      overrideType = "instruction_add";
    } else {
      overrideType = "prerequisite_add";
    }

    // Create temporary policy override
    const override: TemporaryPolicyOverride = {
      type: overrideType,
      topicId: topicIdFromArgs,
      content,
    };

    // Set override and reload chat
    // Use flushSync to ensure state update completes before reload
    flushSync(() => {
      simulatorContext?.clearTemporaryPolicyOverride();
      simulatorContext.setTemporaryPolicyOverride(override);
    });
    // Now the override is guaranteed to be applied, reload with new state
    chatContext.reload();
  }, [
    toolName,
    args,
    simulatorContext,
    chatContext,
    messages,
    setMessages,
    getTopicBrief,
    topicId,
  ]);

  const formattedToolName = useMemo(() => {
    return toolName
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }, [toolName]);

  const argsEntries = useMemo(() => {
    return Object.entries(args).filter(
      ([key]) => key !== "toolName" && key !== "topicId"
    );
  }, [args]);

  const lottieOptions = useMemo(
    () =>
      ({
        loop: true,
        autoplay: true,
        animationData: improveChatLoader,
        rendererSettings: {
          preserveAspectRatio: "xMidYMid slice",
        },
      } as LottieOptions),
    []
  );

  return (
    <Flex align="start" gap="3" className={styles.toolCard}>
      <div className={styles.lottieWrapper}>
        <LottieDisplay
          options={lottieOptions}
          height={50}
          width={50}
          isClickToPauseDisabled={true}
        />
      </div>
      <Flex direction="column" gap="2" style={{ flex: 1 }}>
        <Text size="2" weight="bold" className={styles.toolName}>
          {formattedToolName}
        </Text>
        <Flex direction="column" gap="2">
          {argsEntries.map(([key, value]) => (
            <Flex direction="column" gap="1" key={key}>
              <Text size="1" weight="medium" className={styles.argKey}>
                {key.replace(/_/g, " ")}:
              </Text>
              <Text size="2" className={styles.argValue}>
                {typeof value === "string" ? value : JSON.stringify(value)}
              </Text>
            </Flex>
          ))}
        </Flex>
        {isSuggestionTool && (
          <Flex direction="column" gap="2">
            <Button
              onClick={handlePreview}
              variant="outline"
              disabled={isDisabled || isLoading}
            >
              Preview in Chat
            </Button>
            <Button
              onClick={handleSaveChanges}
              variant="solid"
              highContrast
              disabled={isDisabled || isLoading}
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}
